import { Panel } from '@prisjakt/ui/panel';
import queryString from 'query-string-for-all';
import React, { useCallback, useContext } from 'react';
import { useLocation } from 'react-router';
import { AppContext } from 'src/components/app-context';
import { Breadcrumbs } from '@prisjakt/ui/breadcrumbs';
import { NumberPagination } from 'src/components/number-pagination';
import { PageContent, PageHeader } from 'src/components/page';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';
import { useMatchedRoute } from 'src/hooks/use-matched-route';
import Loader from 'src/components/loader';
import type { RequiredNotNull } from 'src/utils/types';
import type { PrismicArticlesQueryQuery } from '../../types';
import { plainText } from '../editorial-content/components/slices/rich-text';
import FeaturedArticle from '../landing-page/components/featured-article';
import { useEditorialContentListingPageQuery } from './queries/use-editorial-content-listing-page-query';
import * as S from './styles';
import { Link } from 'react-router-dom';
import { BreadCrumbWrapper } from './styles';

const scrollToTop = () => {
  if (IS_BROWSER) {
    window.scrollTo({
      top: 0,
    });
  }
};

const EditorialContentListingPageLoader = () => {
  const route = useMatchedRoute();
  const { data, isLoading, isSuccess } = useEditorialContentListingPageQuery(
    route?.variables
  );

  if (!isLoading && !isSuccess) {
    return null;
  }

  return (
    <PageContent withAds>
      {isLoading ? <Loader /> : null}
      {isSuccess && data.prismicArticles ? (
        <EditorialContentListingPage prismicArticles={data.prismicArticles} />
      ) : null}
    </PageContent>
  );
};

const EditorialContentListingPage = ({
  prismicArticles,
}: RequiredNotNull<Pick<PrismicArticlesQueryQuery, 'prismicArticles'>>) => {
  const { t } = useContext(AppContext);
  const { search } = useLocation();
  const pageInfo = prismicArticles.pageInfo;
  const totalCount = prismicArticles.totalCount;

  const currentPage =
    pageInfo &&
    typeof pageInfo.offset === 'number' &&
    typeof pageInfo.limit === 'number'
      ? Math.floor(pageInfo.offset / pageInfo.limit) + 1
      : 1;

  const generatePaginationUrl = useCallback(
    (pageNumber: number) =>
      `?${queryString.stringify({
        ...queryString.parse(search.substring(1)),
        offset:
          pageInfo?.limit && pageNumber > 1
            ? (pageNumber - 1) * pageInfo.limit
            : undefined,
      })}`,
    [search, pageInfo?.limit]
  );

  return (
    <>
      <BreadCrumbWrapper>
        <Breadcrumbs
          containerProps={{
            onClick: (nextPage: string) => {
              trackInternalNavigationClick({
                scope: InternalNavigationScope.BREADCRUMB,
                nextPage,
              });
            },
          }}
        >
          <Link to="/">{t('common', 'breadcrumb.home')}</Link>
          <span>{t('common', 'article.articles')}</span>
        </Breadcrumbs>
      </BreadCrumbWrapper>

      <PageHeader title={t('common', 'article.articles')} />
      <S.ArticleListWrapper>
        {prismicArticles.nodes?.map(article => {
          return (
            <Panel key={article.id}>
              <FeaturedArticle
                key={article.id}
                pathName={article.pathName}
                imageUrl={article.data.featuredImage?.url}
                title={plainText(article.data.title)}
                preamble={plainText(article.data.metaDescription)}
                linkText={t('landing', 'featured-article.read-more')}
              />
            </Panel>
          );
        })}
      </S.ArticleListWrapper>
      <S.PaginationWrapper>
        <NumberPagination
          currentPage={currentPage}
          totalPages={
            pageInfo.limit && totalCount
              ? Math.ceil(totalCount / pageInfo.limit)
              : 1
          }
          linkToPage={generatePaginationUrl}
          onClick={scrollToTop}
        />
      </S.PaginationWrapper>
    </>
  );
};

export default EditorialContentListingPageLoader;
